import { Link } from 'features/i18n/router'
import { isNil, last } from 'lodash'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { isChatWebsocketEnabled, POLLING_INTERVAL } from 'config'
import { CHANNEL_LIST } from 'features/YayWebSocket/config'
import { useInfinityScrollSimple } from 'hooks'
import { appendList } from 'lib/helpers'
import { ChatRoom } from 'lib/_types'
import {
  useGetChatRoomsMainListQuery,
  useGetChatRoomsTotalChatRequestQuery,
} from 'store/api/yay/chat'
import { MODAL_MODE } from '../config'

import ChatRoomList from 'features/Chat/components/ChatRoomList'
import { YayWebSocketContext } from 'features/YayWebSocket/YayWebSocketContext'
import usePageModal from 'features/Modal/hooks/usePageModal'

import { UsersDiscovery } from './UsersDiscovery'

import './style.scss'

const ChatListModal = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const pollingRef = useRef(null)
  const wsContext = useContext(YayWebSocketContext)

  const [__fromTimestamp, __fromTimestampSet] = useState('0')
  const [__chatRooms, __chatRoomsSet] = useState([])
  const [__pinnedChatRooms, __pinnedChatRoomsSet] = useState<ChatRoom[]>([])

  const { generatePageModalUrl } = usePageModal()

  const getChatRoomsTotalChatRequestRes = useGetChatRoomsTotalChatRequestQuery()

  const getMainChatRoomListRes = useGetChatRoomsMainListQuery({
    fromTimestamp: __fromTimestamp,
  })

  // I'm trying out a new idea here to have a separated query
  // for loading newest chatrooms, rather than alternating `__fromTimestamp`
  // to `0` and the latest page. Seems to work quite well and easier to picture.
  const getMainChatRoomListLatestRes = useGetChatRoomsMainListQuery({
    fromTimestamp: '0',
  })

  const getMainChatRoomListPinnedRes = useGetChatRoomsMainListQuery({
    fromTimestamp: '0',
  })

  const loadMore = () => {
    const fromTimestamp =
      getMainChatRoomListRes?.data?.next_page_value ??
      (last(getMainChatRoomListRes?.data?.chat_rooms), 'updated_at')

    __fromTimestampSet(fromTimestamp?.toString?.())
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    __chatRoomsSet((prevState) => {
      return appendList(
        prevState,
        [
          ...(getMainChatRoomListLatestRes?.data?.chat_rooms ?? []),
          ...(getMainChatRoomListRes?.data?.chat_rooms ?? []),
        ],
        'updated_at'
      )
    })
  }, [getMainChatRoomListRes, getMainChatRoomListLatestRes])

  useEffect(() => {
    __pinnedChatRoomsSet((prevState) => {
      return appendList(
        prevState,
        getMainChatRoomListPinnedRes?.data?.pinned_chats?.map?.((x) => {
          return {
            ...x,
            _pinned: true,
          }
        }),
        'updated_at'
      )
    })
  }, [getMainChatRoomListPinnedRes])

  const poller = () => {
    pollingRef.current = window.setTimeout(() => {
      getMainChatRoomListLatestRes.refetch()

      poller()
    }, POLLING_INTERVAL)
  }

  useEffect(() => {
    if (!isChatWebsocketEnabled) {
      poller()
    } else if (wsContext.isWebSocketOpen) {
      wsContext.subscribe(CHANNEL_LIST.CHAT_ROOM_CHANNEL)
    }

    return () => {
      if (pollingRef.current) {
        window.clearTimeout(pollingRef.current)
      }

      if (isChatWebsocketEnabled) {
        wsContext.unsubscribe(CHANNEL_LIST.CHAT_ROOM_CHANNEL)
      }
    }
  }, [wsContext.isWebSocketOpen])

  useEffect(() => {
    if (
      wsContext.latestMessage !== null &&
      wsContext.latestMessage?.channel === CHANNEL_LIST.CHAT_ROOM_CHANNEL &&
      wsContext.latestMessage?.message?.event === 'new_message'
    ) {
      getMainChatRoomListLatestRes.refetch()
    }
  }, [wsContext.latestMessage])

  return (
    <div className="ChatListModal Modal__wrapper">
      <div className="Modal__header">
        <Link className="Modal__header__close" to={location.pathname} />

        <h2 className="Modal__header__h2">{t('common.chat')}</h2>

        <div className="Modal__header__right">
          <Link
            to={generatePageModalUrl(MODAL_MODE.NEW_CHAT)}
            className="Button Button--green Button--icon-chat"
          >
            {t('common.chat')}
          </Link>
        </div>
      </div>

      <div className="Modal__body Modal__body--gray">
        <UsersDiscovery />

        {getChatRoomsTotalChatRequestRes?.data?.total > 0 && (
          <Link
            className="ChatListModal__request_chat_section"
            to={generatePageModalUrl(MODAL_MODE.REQUEST_CHAT_LIST)}
          >
            <div className="ChatListModal__request_chat_section__title">
              {t(
                getChatRoomsTotalChatRequestRes?.data?.total > 1
                  ? 'chat_list_modal.request_chat_list.title_plural'
                  : 'chat_list_modal.request_chat_list.title',
                {
                  total: getChatRoomsTotalChatRequestRes?.data?.total,
                }
              )}
            </div>

            <svg
              className="ChatListModal__request_chat_section__icon"
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
            >
              <path
                d="M0.663577 2.00995C0.312105 1.64174 0.312105 1.04476 0.663577 0.676547C1.01505 0.308338 1.5849 0.308338 1.93637 0.676547L7.33637 6.33369C7.67709 6.69063 7.68901 7.26539 7.36341 7.6375L2.41341 13.2946C2.07754 13.6785 1.50823 13.7044 1.14182 13.3526C0.775413 13.0007 0.750661 12.4043 1.08653 12.0204L5.45425 7.02875L0.663577 2.00995Z"
                fill="#BDBDBD"
              />
            </svg>
          </Link>
        )}

        <ChatRoomList
          isLoading={getMainChatRoomListRes?.isFetching}
          chatRooms={[...__pinnedChatRooms, ...__chatRooms]}
          onLoadMore={loadMore}
          isFullyLoaded={isNil(getMainChatRoomListRes?.data?.next_page_value)}
          loadMoreRef={loadMoreRef}
          pinnable
          reloadOnDelete
        />
      </div>
    </div>
  )
}

export default ChatListModal
